import { doc, getDoc, setDoc } from 'firebase/firestore';
import { db, auth } from './firebase';

export type LocaleMessages = {
  fr?: string;
  en?: string;
};

export type PlatformConfig = {
  latest: string;       // Version pour suggérer une mise à jour (non bloquante)
  min: string;          // Seuil minimal supporté si enforceMin = true (bloquant)
  enforceMin: boolean;  // Si true et current < min alors blocage
  storeUrl: string;     // Lien vers App/Play Store
  message?: LocaleMessages; // Message personnalisé optionnel
};

export type AppVersionsDoc = {
  client: {
    android: PlatformConfig;
    ios: PlatformConfig;
  };
  driver: {
    android: PlatformConfig;
    ios: PlatformConfig;
  };
  defaults?: {
    enforceMin?: boolean;
    locale?: 'fr' | 'en';
  };
  updatedAt?: number;
  updatedBy?: string;
};

const DEFAULT_PLATFORM_CONFIG: PlatformConfig = {
  latest: '0.0.0',
  min: '0.0.0',
  enforceMin: false,
  storeUrl: '',
  message: {
    fr: 'Une mise à jour est disponible.',
    en: 'An update is available.',
  },
};

export const DEFAULT_APP_VERSIONS: AppVersionsDoc = {
  client: {
    android: { ...DEFAULT_PLATFORM_CONFIG },
    ios: { ...DEFAULT_PLATFORM_CONFIG },
  },
  driver: {
    android: { ...DEFAULT_PLATFORM_CONFIG },
    ios: { ...DEFAULT_PLATFORM_CONFIG },
  },
  defaults: {
    enforceMin: false,
    locale: 'fr',
  },
};

const REF = doc(db, 'config', 'app_versions');

function mergePlatformConfig(src?: Partial<PlatformConfig>): PlatformConfig {
  return {
    latest: src?.latest ?? DEFAULT_PLATFORM_CONFIG.latest,
    min: src?.min ?? DEFAULT_PLATFORM_CONFIG.min,
    enforceMin: typeof src?.enforceMin === 'boolean' ? src!.enforceMin : DEFAULT_PLATFORM_CONFIG.enforceMin,
    storeUrl: src?.storeUrl ?? DEFAULT_PLATFORM_CONFIG.storeUrl,
    message: {
      fr: src?.message?.fr ?? DEFAULT_PLATFORM_CONFIG.message?.fr,
      en: src?.message?.en ?? DEFAULT_PLATFORM_CONFIG.message?.en,
    },
  };
}

function mergeDoc(src?: Partial<AppVersionsDoc>): AppVersionsDoc {
  return {
    client: {
      android: mergePlatformConfig(src?.client?.android),
      ios: mergePlatformConfig(src?.client?.ios),
    },
    driver: {
      android: mergePlatformConfig(src?.driver?.android),
      ios: mergePlatformConfig(src?.driver?.ios),
    },
    defaults: {
      enforceMin: typeof src?.defaults?.enforceMin === 'boolean' ? src!.defaults!.enforceMin! : DEFAULT_APP_VERSIONS.defaults?.enforceMin,
      locale: (src?.defaults?.locale as 'fr' | 'en') ?? DEFAULT_APP_VERSIONS.defaults?.locale,
    },
    updatedAt: src?.updatedAt,
    updatedBy: src?.updatedBy,
  };
}

export async function fetchAppVersions(): Promise<AppVersionsDoc> {
  const snap = await getDoc(REF);
  if (!snap.exists()) {
    return { ...DEFAULT_APP_VERSIONS };
  }
  const data = snap.data() as Partial<AppVersionsDoc>;
  return mergeDoc(data);
}

/**
 * Sauvegarde l&#39;intégralité du document (recommandé depuis l&#39;UI) en fusionnant (merge).
 * Ajoute updatedAt / updatedBy côté serveur.
 */
export async function saveAppVersions(docData: AppVersionsDoc): Promise<void> {
  const updatedAt = Date.now();
  const updatedBy = auth.currentUser?.uid || 'unknown';
  await setDoc(
    REF,
    {
      ...docData,
      updatedAt,
      updatedBy,
    },
    { merge: true }
  );
}

/**
 * Mise à jour partielle du document si besoin (ex: depuis un petit formulaire).
 */
export async function updateAppVersions(partial: Partial<AppVersionsDoc>): Promise<void> {
  const updatedAt = Date.now();
  const updatedBy = auth.currentUser?.uid || 'unknown';
  await setDoc(
    REF,
    {
      ...partial,
      updatedAt,
      updatedBy,
    },
    { merge: true }
  );
}

/**
 * Validation simple SemVer (X.Y.Z). L&#39;UI peut utiliser ceci pour feedback utilisateur.
 */
export function isSemverLike(v: string): boolean {
  return /^\d+\.\d+\.\d+$/.test((v || '').trim());
}
